import { Box, Button, Checkbox, Container, FormControl, FormControlLabel, Radio, RadioGroup, TextField, Typography, useMediaQuery } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { theme } from '../../Resources/Design/theme'
import { fullCenter, generateUID, pastelBlue, pastelDark, pastelOrange, pastelRed, textInputSX, textStyle, titleStyle } from '../Main/constants';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { cs } from "date-fns/locale";
import { Motion } from '../../Components/Motion';
import { useFirestore } from '../../Firebase/FirestoreProvider';
import { useNavigate, useParams } from 'react-router-dom';
import { EditableTypography } from '../../Components/EditableTypography';
import { useDialog } from '../../Routes/DialogManager';
import { onKurzFormSubmitCallable } from '../../Firebase/firebase_init';
import { useAuth } from '../../Firebase/AuthProvider';

const radio = {
    "&.Mui-checked": {
        color: pastelOrange,
    },
}

const label = {
    "& .MuiFormControlLabel-label": {
        ...textStyle
    },
}

const parseStartDate = (str) => {
    const match = str.match(/(\d{1,2})\.\s*(\d{1,2})\./);

    if (!match) return new Date(0);

    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1;

    const yearMatch = str.match(/(\d{4})$/);
    const year = yearMatch ? parseInt(yearMatch[1], 10) : 2026;

    return new Date(year, month, day);
};

const getSortedArray = (array, isAdmin) => {
    if (isAdmin) return array;
    return array.sort((a, b) => {
        // handle empty/null/undefined
        if (!a.text && !b.text) return 0;
        if (!a.text) return 1;   // a goes to end
        if (!b.text) return -1;  // b goes to end

        return parseStartDate(a.text) - parseStartDate(b.text);
    })
}

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

function containsPostCode(value) {
    return /\b\d{3}\s?\d{2}\b/.test(value);
}

const unrequired = ['note'];
const checkErrors = (data) => {
    let isError = false;
    let errors = {};

    if (!isValidEmail(data?.email)) {
        isError = true;
        errors.email = "Neplatný formát emailu";
    }

    if (!containsPostCode(data?.address)) {
        isError = true;
        errors.address = "Musí obsahovat i platné PSČ";
    }

    Object.keys(data).map((d, i) => {
        if (!unrequired.includes(d) && (data[d] === null || (typeof data[d] === "string" && data[d].trim() === ""))) {
            isError = true;
            errors[d] = "Povinný údaj";
        }
    })

    return { isError, errors };
}

const DEFAULT_VALUES = {
    date: null,
    email: "",
    phone: "",
    name: "",
    surname: "",
    skolka: "",
    name_parent: "",
    surname_parent: "",
    address: "",
    note: "",
};


const createBlankCourse = () => ({
    title: "",
    dates: [],
    tag: generateUID(8),
    split: "0",
});

const createBlankDate = () => ({
    text: "",
    ids: [],
    max: 12,
    uid: generateUID(8),
});

export default function CoursesForm() {
    const { setLoading, setDialog, loading } = useDialog();
    const [checkedError, setCheckedError] = useState(false);
    const [checked, setChecked] = useState(false);
    const { isAdmin } = useAuth();

    const [checkedZOOError, setCheckedZOOError] = useState(false);
    const [checkedZOO, setCheckedZOO] = useState(false);

    const { program: initProgram } = useParams();
    const phone = useMediaQuery(theme.breakpoints.only('xs'));
    const [program, setProgram] = useState(0);
    const [termin, setTermin] = useState(0);
    const [errors, setErrors] = useState({});
    const [values, setValues] = useState(DEFAULT_VALUES);
    const { data, changeData } = useFirestore();
    const programy = data?.courses_list || [];
    const choosedProgram = programy?.[program] ?? {};
    const choosedDates = getSortedArray(choosedProgram.dates || [], isAdmin);
    const canSplit = !Boolean(!choosedProgram?.split || choosedProgram.split == 0);
    const [zpUhrady, setZpUhrady] = useState(0);
    const nahradniku_max = data?.nahradniku_max_kurzy || 0;

    const navigate = useNavigate();

    const changeValue = (val) => {
        try {
            const onlyDigits = val.replace(/\D+/g, "");
            return parseInt(onlyDigits || 0);
        } catch (e) {
            setDialog("Zadejte platné číslo");
            return 0;
        }
    }

    const getValue = (name) => {
        return values?.[name] || "";
    }

    const hasError = (name) => {
        return errors?.[name] ? true : false;
    }

    const getErrorText = (name) => {
        return errors?.[name] || "";
    }

    const handleChange = (e) => {
        const value = e.target.value;
        const name = e.target.name;
        setValues(prev => ({
            ...prev,
            [name]: value
        }));

        if (hasError(name)) {
            setErrors(prev => ({
                ...prev,
                [name]: undefined
            }));
        }
    }

    const setData = (name, value) => {
        setValues(prev => ({
            ...prev,
            [name]: value
        }));
    }

    function timestampToDateString(value) {
        try {
            let date;

            // Firestore Timestamp
            if (value && typeof value.toDate === "function") {
                date = value.toDate();
            }
            // JS Date
            else if (value instanceof Date) {
                date = value;
            }
            // Milliseconds number
            else if (typeof value === "number") {
                date = new Date(value);
            }
            // ISO string
            else if (typeof value === "string") {
                const parsed = new Date(value);
                if (!isNaN(parsed.getTime())) date = parsed;
            } else if (value?.seconds || value?._seconds) {
                date = new Date((value?.seconds || value?._seconds) * 1000);
            }

            // Invalid fallback
            if (!date || isNaN(date.getTime())) return "";

            const dd = String(date.getDate()).padStart(2, "0");
            const mm = String(date.getMonth() + 1).padStart(2, "0");
            const yyyy = date.getFullYear();

            return `${dd}.${mm}.${yyyy}`;
        } catch (e) {
            console.error("Timestamp format error:", e);
            return "";
        }
    }

    const scrollToFirstError = (errors) => {
        try {
            if (errors) {
                const keys = Object.keys(errors);
                if (keys.length > 0) {
                    const id = keys[0];
                    const el = document?.getElementById(id);
                    if (el) {
                        el.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                        });
                    };
                }
            }
        } catch (e) {
            console.log("Scroll failed ", e);
        }
    }


    /*
    data example:
    
    {
    "date": "2009-01-01T23:00:00.000Z",
    "email": "micanek.jakub@seznam.cz",
    "phone": "739880528",
    "name": "Petra",
    "surname": "Kalašová",
    "skolka": "Veslarska",
    "address": "Horacke namesti 3",
    "program": "safari",
    "termin": "5kjXLSwu"
    }
    */
    const handleSubmit = () => {
        if (loading) return;

        if (!checked || !checkedZOO) {
            if (!checked) {
                setCheckedError(true);
            }

            if (!checkedZOO) {
                setCheckedZOOError(true);
            }
            return;
        }



        const { isError, errors } = checkErrors(values);
        if (isError) {
            console.log("Errors", errors);
            setErrors(errors);
            scrollToFirstError(errors);
            return;
        }

        const programTag = choosedProgram.tag;
        const dateUid = choosedDates[termin].uid;

        let date = values?.date;
        if (date instanceof Date) {
            if (isNaN(date) || date == "Invalid Date") {
                const errorsWithDate = { ...errors, date: 'Neplatné datum (zadejte d.M.yyyy)' };
                setErrors(errorsWithDate);
                scrollToFirstError(errorsWithDate);
                return;
            }

            const year = date.getFullYear();
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const day = date.getDate().toString().padStart(2, '0');
            const localISO = `${year}-${month}-${day}`;
            date = localISO;
        }

        const data = {
            ...values,
            program: programTag,
            termin: dateUid,
            date,
            single_purchase: canSplit ? Boolean(zpUhrady === 0) : true,
        }

        setLoading(true);
        onKurzFormSubmitCallable(data).then((result) => {
            console.log("Result", result);
            /* if (window?.gtag) {
                 window.gtag('event', 'registration_kurz_gtag');
             }
 
             if (window?.gtagSendEventKurz) {
                 window.gtagSendEventKurz();
             }
 
             logAnalytics("reserved_kurz");
 
             if (window) {
                 window.dataLayer = window.dataLayer || [];
                 window.dataLayer.push({
                     event: "registration_kurz_complete"
                 });
             }*/

            const uid = result?.userId || result?.data?.userId;
            if (uid) {
                setTimeout(() => {
                    navigate(`/result/${uid}?kurz=ano`);
                }, 200);
                return;
            }
            setDialog('Rezervace byla úspěšná. Údaje k platbě jsme vám zaslali e-mailem (zkontrolujte i spam / hromadné zprávy).');
            setValues(DEFAULT_VALUES);
            console.log(result);
        }).catch((err) => {
            console.error(err);
            setDialog("Omlouváme se, online rezervaci nebylo možné provést, pro rezervaci nám prosím zavolejte.");
        }).finally(() => {
            setLoading(false);
        });
    }

    useEffect(() => {
        if (initProgram) {
            programy.forEach((p, i) => {
                if (p.tag === initProgram) {
                    setProgram(i);
                }
            });
        }
    }, [programy]);


    return (
        <Container maxWidth='lg' sx={{ pb: '120px', pt: { xs: '100px', sm: '130px' } }}>
            <Box sx={{ width: '100%', ...fullCenter }}>
                <EditableTypography component={'h1'} fireKey={'kurz_form_title'} sx={{ ...titleStyle, my: '120px', textAlign: 'center' }} />
            </Box>

            <Container disableGutters={phone} maxWidth='sm'>
                <Box
                    component="form"
                    noValidate
                    autoComplete="off"
                    sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}
                >

                    <EditableTypography fireKey={'kurz_form_programs'} bottom={0} right={-50} sx={{ ...textStyle, fontSize: '22px', fontWeight: 'bold', mb: '18px' }} />
                    <EditableTypography fireKey={'kurz_chooseplace'} bottom={0} right={-50} sx={{ ...textStyle, mb: '18px' }} />

                    <FormControl >
                        <RadioGroup
                            aria-labelledby="demo-radio-buttons-group-label"
                            name="radio-buttons-group"
                            value={program}
                            onChange={(e) => {
                                console.log(typeof e.target.value);
                                setProgram(e.target.value);
                            }}
                        >
                            {programy.map((d, i) => <React.Fragment key={"PCF" + i}>
                                <FormControlLabel value={i} control={<Radio sx={radio} />}
                                    label={<>
                                        <Box sx={{ position: 'relative', width: '100%' }}>
                                            <EditableTypography sx={{ ...textStyle, lineHeight: '20px', fontWeight: 'bold', minHeight: '20px' }} path={`courses_list.${i}.title`} bottom={-10} right={-50} />

                                            {isAdmin && <Box component='span'
                                                sx={{
                                                    width: '34px', height: '34px', maxWidth: '34px', maxHeight: '34px', minWidth: '34px', borderRadius: '50%',
                                                    bgcolor: pastelRed,
                                                    scale: 1,
                                                    transition: 'scale 0.2s ease-in-out',
                                                    "&:hover": { scale: 1.05 }, ml: '12px',
                                                    zIndex: 14, cursor: 'pointer', ...fullCenter, mt: '12px',
                                                    position: 'absolute', right: -90, bottom: -10
                                                }}
                                                onClick={(e) => {
                                                    if (isAdmin) {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                    }
                                                    const newOftenAsked = programy.filter((_, index) => index !== i);
                                                    changeData('courses_list', newOftenAsked);

                                                }} onMouseDown={(e) => {
                                                    if (isAdmin) {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                    }
                                                }}>

                                                <Typography sx={{ fontWeight: 'bold', fontSize: '28px', mb: '4px', color: 'white' }}>-</Typography>
                                            </Box>}
                                            <Box sx={{ position: 'absolute', right: -140, top: 0 }}>
                                                {isAdmin && <EditableTypography sx={{ ...textStyle, lineHeight: '20px', fontWeight: 'bold', minHeight: '20px' }}
                                                    path={`courses_list.${i}.split`} bottom={-10} right={-50} comment="ROZLOŽENÍ PLATEB: 0 = NE, 1 = ANO"
                                                    modifyVisible={(v) => { if (v == 0) return "NE"; return "ANO"; }}
                                                    changeValue={(v) => { if (v == 0) return "0"; return "1"; }} />}
                                            </Box>
                                        </Box>
                                    </>}
                                    sx={label} />


                            </React.Fragment>)}


                        </RadioGroup>
                    </FormControl>
                    {isAdmin && <Box sx={{ display: 'flex', flexDirection: 'row' }}><Box component='span'
                        sx={{
                            width: '34px', height: '34px', maxWidth: '34px', maxHeight: '34px', minWidth: '34px', borderRadius: '50%',
                            bgcolor: pastelBlue,
                            scale: 1,
                            transition: 'scale 0.2s ease-in-out',
                            "&:hover": { scale: 1.05 },
                            zIndex: 14, cursor: 'pointer', ...fullCenter, mt: '12px'
                        }}
                        onClick={(e) => {
                            if (isAdmin) {
                                e.preventDefault();
                                e.stopPropagation();
                            }
                            const newOftenAsked = [...programy, createBlankCourse()]
                            changeData('courses_list', newOftenAsked);

                        }} onMouseDown={(e) => {
                            if (isAdmin) {
                                e.preventDefault();
                                e.stopPropagation();
                            }
                        }}>

                        <Typography sx={{ fontWeight: 'bold', fontSize: '28px', mb: '4px' }}>+</Typography>
                    </Box>

                    </Box>}
                    <EditableTypography fireKey={'kurz_form_dates'} bottom={0} right={-50} sx={{ ...textStyle, fontSize: '22px', fontWeight: 'bold', pt: '48px', mb: '18px' }} />

                    <FormControl >
                        <RadioGroup
                            aria-labelledby="demo-radio-buttons-group-label"
                            name="radio-buttons-group"
                            value={termin}
                            onChange={(e) => {
                                setTermin(e.target.value);
                            }}
                        >
                            {choosedDates.map((d, i) => {
                                const registered = d?.ids?.length || 0;

                                const max_to_register = d?.max || 12;
                                const max_total = max_to_register + nahradniku_max;

                                const maxed_out = Boolean(registered >= max_total);
                                const only_nahradnici = Boolean(registered >= max_to_register);

                                return <React.Fragment key={"CDCF" + i}>
                                    <FormControlLabel value={i} control={<Radio sx={radio} />}
                                        label={<Box sx={{ display: 'flex', flexDirection: 'row' }}>
                                            <EditableTypography sx={{ ...textStyle, lineHeight: '20px', fontWeight: 'bold', minHeight: '20px' }}
                                                path={`courses_list.${program}.dates.${i}.text`} bottom={-10} right={-50} />
                                            {isAdmin && <Box component='span'
                                                sx={{
                                                    width: '34px', height: '34px', maxWidth: '34px', maxHeight: '34px', minWidth: '34px', borderRadius: '50%',
                                                    bgcolor: pastelRed,
                                                    scale: 1,
                                                    transition: 'scale 0.2s ease-in-out',
                                                    "&:hover": { scale: 1.05 }, ml: '12px',
                                                    zIndex: 14, cursor: 'pointer', ...fullCenter, mt: '12px',
                                                    position: 'absolute', right: -90, bottom: -10
                                                }}
                                                onClick={(e) => {
                                                    if (isAdmin) {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                    }
                                                    const newOftenAsked = choosedDates.filter((_, index) => index !== i);
                                                    changeData(null, newOftenAsked, `courses_list.${program}.dates`);

                                                }} onMouseDown={(e) => {
                                                    if (isAdmin) {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                    }
                                                }}>

                                                <Typography sx={{ fontWeight: 'bold', fontSize: '28px', mb: '4px', color: 'white' }}>-</Typography>
                                            </Box>}
                                            {Boolean(only_nahradnici && !maxed_out) && <Typography sx={{ ml: '8px', lineHeight: '20px', fontWeight: 'bold', fontSize: '16px', color: '#474747' }}>(pouze náhradníci)</Typography>}
                                            {Boolean(maxed_out) && <Typography sx={{ ml: '8px', lineHeight: '20px', fontWeight: 'bold', fontSize: '16px', color: '#474747' }}>(plný)</Typography>}
                                        </Box>}
                                        sx={label} />

                                </React.Fragment>
                            })}
                        </RadioGroup>
                    </FormControl>
                    {isAdmin && <Box sx={{ display: 'flex', flexDirection: 'row' }}><Box component='span'
                        sx={{
                            width: '34px', height: '34px', maxWidth: '34px', maxHeight: '34px', minWidth: '34px', borderRadius: '50%',
                            bgcolor: pastelBlue,
                            scale: 1,
                            transition: 'scale 0.2s ease-in-out',
                            "&:hover": { scale: 1.05 },
                            zIndex: 14, cursor: 'pointer', ...fullCenter, mt: '12px'
                        }}
                        onClick={(e) => {
                            if (isAdmin) {
                                e.preventDefault();
                                e.stopPropagation();
                            }
                            const newOftenAsked = [...choosedDates, createBlankDate()]
                            changeData(null, newOftenAsked, `courses_list.${program}.dates`);

                        }} onMouseDown={(e) => {
                            if (isAdmin) {
                                e.preventDefault();
                                e.stopPropagation();
                            }
                        }}>

                        <Typography sx={{ fontWeight: 'bold', fontSize: '28px', mb: '4px' }}>+</Typography>
                    </Box>

                    </Box>}
                    <EditableTypography fireKey={'kurz_termtext'} bottom={0} right={-50} sx={{ ...textStyle, mt: '12px' }} />

                    <EditableTypography fireKey={'kurz_form_child'} bottom={0} right={-50} sx={{ ...textStyle, fontSize: '22px', fontWeight: 'bold', pt: '48px', mb: '18px' }} />
                    <TextField variant="standard" label="Jméno dítěte" type='text' name='name' id='name' onChange={handleChange} value={getValue("name")} helperText={getErrorText("name")} error={hasError("name")} inputProps={{ autoComplete: "given-name", maxLength: 128 }} required sx={{
                        ...textInputSX,
                        mb: '16px',
                    }} />
                    <TextField variant="standard" label="Příjmení dítěte" type='text' name='surname' id='surname' onChange={handleChange} value={getValue("surname")} helperText={getErrorText("surname")} error={hasError("surname")} inputProps={{ autoComplete: "family-name", maxLength: 128 }} required sx={{
                        ...textInputSX,
                        mb: '16px',
                    }} />

                    <LocalizationProvider adapterLocale={cs} dateAdapter={AdapterDateFns}>
                        <DatePicker
                            disableMaskedInput
                            label="Datum narození dítěte"
                            inputFormat="d.M.yyyy"
                            value={getValue('date')}
                            onChange={(newValue) => {
                                setData('date', newValue);
                            }}
                            renderInput={(params) => <TextField {...params} variant="standard" id='date' required
                                error={hasError("date")}
                                helperText={getErrorText("date")} sx={{
                                    ...textInputSX,
                                    mb: '16px'
                                }}
                                onChange={(e) => {
                                    params.inputProps?.onChange?.(e);
                                    if (hasError("date")) {
                                        setErrors(prev => ({
                                            ...prev,
                                            date: undefined
                                        }));
                                    }
                                }}
                            />}
                        />
                    </LocalizationProvider>

                    <TextField variant="standard" label="Mateřská škola, kterou dítě navštěvuje:" type='text' name='skolka' id='skolka' onChange={handleChange} value={getValue("skolka")} helperText={getErrorText("skolka")} error={hasError("skolka")} inputProps={{ maxLength: 128 }} required sx={{
                        ...textInputSX,
                        mb: '16px',
                    }} />

                    <EditableTypography fireKey={'kurz_form_contact'} bottom={0} right={-50} sx={{ ...textStyle, fontSize: '22px', fontWeight: 'bold', mb: '18px', mt: '42px' }} />
                    <TextField variant="standard" label="Jméno" type='text' name='name_parent' id='name_parent' onChange={handleChange} value={getValue("name_parent")} helperText={getErrorText("name_parent")} error={hasError("name_parent")} inputProps={{ autoComplete: "given-name", maxLength: 128 }} required sx={{
                        ...textInputSX,
                        mb: '16px',
                    }} />
                    <TextField variant="standard" label="Příjmení" type='surname_parent' name='surname_parent' id='surname_parent' onChange={handleChange} value={getValue("surname_parent")} helperText={getErrorText("surname_parent")} error={hasError("surname_parent")} inputProps={{ autoComplete: "family-name", maxLength: 128 }} required sx={{
                        ...textInputSX,
                        mb: '16px',
                    }} />
                    <TextField variant="standard" label="Adresa (včetně PSČ)" type='text' name='address' id='address' onChange={handleChange} value={getValue("address")} helperText={getErrorText("address")} error={hasError("address")} inputProps={{ autoComplete: "address", maxLength: 128 }} required sx={{
                        ...textInputSX,
                        mb: '16px',
                    }} />
                    <TextField variant="standard" label="Email" type='email' name='email' id='email' onChange={handleChange} value={getValue("email")} helperText={getErrorText("email")} error={hasError("email")} inputProps={{ autoComplete: "email", maxLength: 128 }} required sx={{
                        ...textInputSX,
                        mb: '16px',
                    }} />
                    <TextField variant="standard" label="Telefonní číslo" type='tel' name='phone' id='phone'
                        onChange={handleChange} value={getValue("phone")} helperText={getErrorText("phone")} error={hasError("phone")}
                        required inputProps={{
                            autoComplete: "tel", maxLength: 128
                        }}

                        sx={{
                            ...textInputSX,
                        }} />

                    <EditableTypography fireKey={'kurzy_form_price'} bottom={0} right={-50} sx={{ ...textStyle, fontSize: '22px', fontWeight: 'bold', pt: '42px', mb: '18px' }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {/*<EditableTypography comment='Program-specific text' modifyVisible={(v) => applySale(v, Boolean(values.sibling))} path={`programs_list.${program}.price`} sx={{ ...textStyle }} changeValue={changeValue} bottom={0} right={'auto'} left={-50} />*/}
                        <EditableTypography fireKey={'kurzy_form_price_text'} bottom={0} right={-50} sx={{ ...textStyle }} />
                        {canSplit && <EditableTypography fireKey={'kurzy_form_price_text_split'} bottom={0} right={-50} sx={{ ...textStyle }} />}
                    </Box>
                    {canSplit &&
                        <>
                            <EditableTypography fireKey={'kurz_zpuhrady'} bottom={0} right={-50} sx={{ ...textStyle, fontSize: '22px', fontWeight: 'bold', pt: '48px', mb: '18px' }} />
                            <FormControl sx={{}}>
                                <RadioGroup
                                    aria-labelledby="demo-radio-buttons-group-label"
                                    name="radio-buttons-group"
                                    value={zpUhrady}
                                    onChange={(e) => {
                                        setZpUhrady(e.target.value);
                                    }}
                                >

                                    <FormControlLabel value={0} control={<Radio sx={radio} />}
                                        label={<><EditableTypography sx={{ ...textStyle, lineHeight: '20px', minHeight: '20px' }} fireKey={`kurz_single`} bottom={-10} right={-50} />

                                        </>}
                                        sx={label} />
                                    <FormControlLabel value={1} control={<Radio sx={radio} />}
                                        label={<><EditableTypography sx={{ ...textStyle, lineHeight: '20px', minHeight: '20px' }} fireKey={`kurz_double`} bottom={-10} right={-50} />

                                        </>}
                                        sx={label} />


                                </RadioGroup>
                            </FormControl>
                            <EditableTypography fireKey={'kurzy_form_zpuhrady'} bottom={0} right={-50} sx={{ ...textStyle, mt: '18px' }} />
                        </>}


                    <EditableTypography fireKey={'notes_title'} bottom={0} right={-50} sx={{ ...textStyle, fontSize: '22px', fontWeight: 'bold', pt: '48px', mb: '18px' }} />
                    <TextField variant="standard" label="Volitelné poznámky" id='note' onChange={handleChange} error={hasError("note")} helperText={getErrorText("note")} value={getValue("note")} name='note' type='text' multiline sx={{
                        ...textInputSX,
                    }} inputProps={{ maxLength: 256 }} />

                    <Box sx={{
                        mt: '52px', display: 'flex', flexDirection: 'row', ...fullCenter, py: '12px',
                        ...(checkedError ? { border: `1px solid ${pastelRed}`, borderRadius: '8px', bgcolor: `#FEF2F2` } : { border: `1px solid transparent` }),
                    }}>
                        <Checkbox
                            checked={checked}
                            onChange={(e) => {
                                setCheckedError(false);
                                setChecked(e.target.checked);
                            }}
                            inputProps={{
                                'aria-label': 'controlled',
                                id: 'form-check',
                            }}
                            sx={{
                                color: checkedError ? pastelRed : "grey",
                                "&.Mui-checked": {
                                    color: pastelOrange,
                                },

                            }}
                        />
                        <Typography sx={{ ...textStyle, textDecoration: 'none', ml: { xs: '6px', sm: '24px' } }}>
                            Souhlasím se <a href={'/podminky_kurzy'} target='_blank' rel="noreferrer" style={{ textDecoration: 'none', color: '#2B7FFF' }}>Všeobecnými obchodními podmínkami</a> pro kurzy a potvrzuji, že jsem se s nimi před odesláním přihlášky seznámil/a.
                        </Typography>

                    </Box>
                    <Box sx={{
                        mb: '52px', display: 'flex', flexDirection: 'row', ...fullCenter, py: '12px',
                        ...(checkedZOOError ? { border: `1px solid ${pastelRed}`, borderRadius: '8px', bgcolor: `#FEF2F2` } : { border: `1px solid transparent` }),
                    }}>
                        <Checkbox
                            checked={checkedZOO}
                            onChange={(e) => {
                                setCheckedZOOError(false);
                                setCheckedZOO(e.target.checked);
                            }}
                            inputProps={{
                                'aria-label': 'controlled',
                                id: 'form-check',
                            }}
                            sx={{
                                color: checkedZOOError ? pastelRed : "grey",
                                "&.Mui-checked": {
                                    color: pastelOrange,
                                },

                            }}
                        />
                        <Typography sx={{ ...textStyle, textDecoration: 'none', ml: { xs: '6px', sm: '24px' } }}>
                            Potvrzuji, že jsem se seznámil/a se <a href={'/zasady_kurzy'} target='_blank' rel="noreferrer" style={{ textDecoration: 'none', color: '#2B7FFF' }}>Zásadami ochrany osobních údajů</a>.
                        </Typography>
                    </Box>

                    <Box sx={{ width: '100%', ...fullCenter, pb: '32px' }}>
                        <Motion scale={1.01}>
                            <Box component={'button'} type='button' aria-label='Závazně přihlásit' sx={{
                                textDecoration: 'none',
                                height: "60px",
                                minWidth: "200px",
                                width: 'fit-content',
                                borderRadius: "35px", bgcolor: pastelOrange,
                                textAlign: "center",
                                mb: "0px",
                                border: 'none',
                                cursor: 'pointer',
                                ...fullCenter,
                            }}
                                onClick={() => {
                                    handleSubmit();
                                }}
                            >
                                <EditableTypography
                                    fireKey={'register_text'}
                                    noPropagation
                                    variant="buttonText"
                                    sx={{ ...textStyle, color: 'white', fontWeight: 'bold' }}
                                />
                            </Box>
                        </Motion>
                    </Box>
                </Box>

            </Container>
        </Container>
    )
}
